import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Modal, message, Descriptions, Tabs } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const GOLD = '#B8892A';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';
const SEAL = '#8C1F1F';

const STATUS_MAP = {
  pending:    { labelKey: 'consignment.statusPending',   color: 'orange' },
  reviewing:  { labelKey: 'consignment.statusReviewing', color: 'processing' },
  active:     { labelKey: 'consignment.statusApproved',  color: 'green' },
  sold:       { labelKey: 'consignment.statusSold',      color: 'blue' },
  cancelled:  { labelKey: 'consignment.statusCancelled', color: 'default' },
  rejected:   { labelKey: 'consignment.statusRejected',  color: 'red' },
};

const CHANNELS = [
  {
    key: 'submit',
    icon: '📦',
    titleKey: 'consignment.channelSubmitTitle',
    descKey: 'consignment.channelSubmitDesc',
    subKey: 'consignment.channelSubmitSub',
    route: '/consignment/submit',
  },
  {
    key: 'store',
    icon: '🏪',
    titleKey: 'consignment.channelStoreTitle',
    descKey: 'consignment.channelStoreDesc',
    subKey: 'consignment.channelStoreSub',
    route: '/consignment/submit?delivery=store',
  },
  {
    key: 'reverse',
    icon: '🇯🇵',
    titleKey: 'consignment.channelReverseTitle',
    descKey: 'consignment.channelReverseDesc',
    subKey: 'consignment.channelReverseSub',
    route: '/reverse-apply',
  },
];

const RECENT_SALES = [
  { name: 'Rolex GMT-Master II', price: 88000, days: 3, discountKey: 'consignment.discount95' },
  { name: 'Hermès Birkin 30', price: 128000, days: 5, discountKey: 'consignment.discount92' },
  { name: 'Chanel Classic Flap', price: 48000, days: 2, discountKey: 'consignment.discount88' },
  { name: 'Cartier Love Ring', price: 28000, days: 1, discountKey: 'consignment.discount97' },
];

const FAQ_KEYS = [
  { qKey: 'consignment.faq1Q', aKey: 'consignment.faq1A' },
  { qKey: 'consignment.faq2Q', aKey: 'consignment.faq2A' },
  { qKey: 'consignment.faq3Q', aKey: 'consignment.faq3A' },
  { qKey: 'consignment.faq4Q', aKey: 'consignment.faq4A' },
  { qKey: 'consignment.faq5Q', aKey: 'consignment.faq5A' },
  { qKey: 'consignment.faq6Q', aKey: 'consignment.faq6A' },
];

const WHY_CHOOSE = [
  { icon: '🔍', titleKey: 'consignment.whyInspection', descKey: 'consignment.whyInspectionDesc' },
  { icon: '📊', titleKey: 'consignment.whyAiPricing', descKey: 'consignment.whyAiPricingDesc' },
  { icon: '🌐', titleKey: 'consignment.whyAuctionChannel', descKey: 'consignment.whyAuctionChannelDesc' },
  { icon: '⚡', titleKey: 'consignment.whyFastDeal', descKey: 'consignment.whyFastDealDesc' },
  { icon: '🛡', titleKey: 'consignment.whySafety', descKey: 'consignment.whySafetyDesc' },
  { icon: '💰', titleKey: 'consignment.whyHighReturn', descKey: 'consignment.whyHighReturnDesc' },
];

export default function Consignment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [tab, setTab] = useState('entry');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [myOrders, setMyOrders] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ price_cny: '', min_price_cny: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(-1);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchItems = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get('/consignments', { params: { page: p, limit: 10 } });
      setItems(res.data.data);
      setTotal(res.data.total);
      setPage(p);
    } catch { message.error(t('common.failed')); }
    finally { setLoading(false); }
  }, []);

  const fetchMyOrders = useCallback(async () => {
    try {
      const res = await api.get('/market/orders/list', { params: { role: 'seller', limit: 50 } });
      setMyOrders(res.data.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (tab === 'my') { fetchItems(1); fetchMyOrders(); }
  }, [tab, fetchItems, fetchMyOrders]);

  const handleCancel = async (id) => {
    try {
      await api.patch(`/consignments/${id}`, { status: 'cancelled' });
      message.success(t('consignment.cancelled'));
      fetchItems(page);
    } catch (e) { message.error(e.response?.data?.error || t('common.failed')); }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({ price_cny: item.price_cny || '', min_price_cny: item.min_price_cny || '' });
  };

  const handleEditSave = async () => {
    if (!editForm.price_cny || Number(editForm.price_cny) <= 0) { message.warning(t('consignment.validPriceRequired')); return; }
    setEditSaving(true);
    try {
      await api.patch(`/consignments/${editItem.id}`, {
        price_cny: Number(editForm.price_cny),
        min_price_cny: editForm.min_price_cny ? Number(editForm.min_price_cny) : null,
      });
      message.success(t('consignment.priceUpdated'));
      setEditItem(null);
      fetchItems(page);
    } catch (e) { message.error(e.response?.data?.error || t('common.failed')); }
    finally { setEditSaving(false); }
  };

  const px = isMobile ? 16 : 48;

  return (
    <div style={{ background: BG, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        background: '#fff', borderBottom: `1px solid ${BD}`, padding: `24px ${px}px 0`,
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 4, background: SEAL,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#F5E6D0', fontSize: 8, fontWeight: 700, lineHeight: 1.1, textAlign: 'center', letterSpacing: 1,
            }}>云川<br/>中古</div>
            <span style={{ fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 700, color: INK }}>{t('nav.consignment')}</span>
          </div>
          <div style={{ fontSize: 13, color: MUTED, marginBottom: 16 }}>{t('consignment.slogan')}</div>

          <Tabs activeKey={tab} onChange={setTab} items={[
            { key: 'entry', label: t('consignment.startConsignment') },
            { key: 'my',    label: t('consignment.myConsignments') },
          ]} />
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: `20px ${px}px 40px` }}>
        {tab === 'entry' && (
          <>
            {/* ── 估价引导卡片 ── */}
            <div style={{
              padding: isMobile ? '24px 16px' : '28px 32px', background: 'linear-gradient(135deg, #2A2118 0%, #3D2E1E 100%)',
              borderRadius: 12, marginBottom: 20, color: '#fff', position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(184,137,42,.15)' }} />
              <div style={{ position: 'relative' }}>
                <div style={{ fontSize: 11, color: 'rgba(245,230,208,.7)', letterSpacing: 2, marginBottom: 8 }}>{t('consignment.freeEstimate')}</div>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, marginBottom: 6, lineHeight: 1.3 }}>
                  {t('consignment.estimateQuestion')}
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,.65)', marginBottom: 16 }}>
                  {t('consignment.estimateDesc')}
                </div>
                <button onClick={() => navigate('/consignment/submit')} style={{
                  padding: '10px 28px', borderRadius: 6, border: 'none',
                  background: GOLD, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(184,137,42,.4)',
                }}>{t('consignment.freeOnlineEstimate')}</button>
              </div>
            </div>

            {/* ── 最近成交 ── */}
            <div style={{
              background: '#fff', border: `1px solid ${BD}`, borderRadius: 10, padding: '18px 20px', marginBottom: 20,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <span style={{ fontWeight: 600, fontSize: 15, color: INK }}>{t('consignment.recentSales')}</span>
                <span style={{ fontSize: 12, color: MUTED }}>{t('consignment.last7Days')}</span>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: 10,
              }}>
                {RECENT_SALES.map((s, i) => (
                  <div key={i} style={{
                    padding: '12px', background: '#FAF8F5', borderRadius: 8,
                    border: `1px solid ${BD}40`,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: INK, marginBottom: 6,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: GOLD, fontFamily: 'var(--mono)' }}>
                      ¥{s.price.toLocaleString()}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                      <span style={{ fontSize: 10, color: MUTED }}>{t('consignment.daysSold', { days: s.days })}</span>
                      <span style={{ fontSize: 10, color: SEAL, fontWeight: 600 }}>{t(s.discountKey)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── 寄售渠道卡片 ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {CHANNELS.map(ch => (
                <button key={ch.key} onClick={() => navigate(ch.route)} style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: isMobile ? '20px 16px' : '24px 28px',
                  background: '#fff', border: `1px solid ${BD}`, borderRadius: 10,
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'border-color .2s, box-shadow .2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.boxShadow = '0 4px 20px rgba(184,137,42,.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <span style={{ fontSize: 32 }}>{ch.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: INK, marginBottom: 4 }}>{t(ch.titleKey)}</div>
                    <div style={{ fontSize: 13, color: MUTED }}>{t(ch.descKey)}</div>
                    <div style={{ fontSize: 11, color: '#A89070', marginTop: 2 }}>{t(ch.subKey)}</div>
                  </div>
                  <span style={{ fontSize: 18, color: MUTED }}>›</span>
                </button>
              ))}
            </div>

            {/* ── 转卖我的订单 ── */}
            <div style={{
              marginTop: 16, padding: '20px 24px', background: '#fff', border: `1px solid ${BD}`,
              borderRadius: 10, cursor: 'pointer',
            }} onClick={() => navigate('/consignment/submit?source=order')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>🔄</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: INK }}>{t('consignment.resellOrder')}</div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{t('consignment.resellOrderDesc')}</div>
                </div>
                <span style={{ fontSize: 18, color: MUTED }}>›</span>
              </div>
            </div>

            {/* ── 为什么选择云川中古 ── */}
            <div style={{ marginTop: 28 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: INK, marginBottom: 4, textAlign: 'center' }}>{t('consignment.whyChooseUs')}</div>
              <div style={{ fontSize: 12, color: MUTED, marginBottom: 16, textAlign: 'center' }}>{t('consignment.whyChooseUsSub')}</div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                gap: 12,
              }}>
                {WHY_CHOOSE.map(w => (
                  <div key={w.titleKey} style={{
                    padding: '18px 14px', background: '#fff', border: `1px solid ${BD}`,
                    borderRadius: 10, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{w.icon}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 4 }}>{t(w.titleKey)}</div>
                    <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.5 }}>{t(w.descKey)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── FAQ 手风琴 ── */}
            <div style={{ marginTop: 28 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: INK, marginBottom: 16, textAlign: 'center' }}>{t('consignment.faqTitle')}</div>
              <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 10, overflow: 'hidden' }}>
                {FAQ_KEYS.map((faq, i) => (
                  <div key={i}>
                    <button onClick={() => setExpandedFaq(expandedFaq === i ? -1 : i)} style={{
                      width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '14px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
                      borderBottom: i < FAQ_KEYS.length - 1 ? `1px solid ${BD}40` : 'none',
                      textAlign: 'left',
                    }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: INK }}>{t(faq.qKey)}</span>
                      <span style={{
                        fontSize: 16, color: MUTED, transform: expandedFaq === i ? 'rotate(180deg)' : 'none',
                        transition: 'transform .2s', flexShrink: 0, marginLeft: 10,
                      }}>▾</span>
                    </button>
                    {expandedFaq === i && (
                      <div style={{
                        padding: '0 20px 14px', fontSize: 13, color: MUTED, lineHeight: 1.7,
                        borderBottom: i < FAQ_KEYS.length - 1 ? `1px solid ${BD}40` : 'none',
                      }}>
                        {t(faq.aKey)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── 寄卖须知 ── */}
            <div style={{ marginTop: 24, padding: '20px 24px', background: '#fff', border: `1px solid ${BD}`, borderRadius: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 14 }}>{t('consignment.noticeTitle')}</div>
              {[
                [t('consignment.noticeInspection'), t('consignment.noticeInspectionDesc')],
                [t('consignment.noticeCommission'), t('consignment.noticeCommissionDesc')],
                [t('consignment.noticeDelivery'), t('consignment.noticeDeliveryDesc')],
                [t('consignment.noticeSettlement'), t('consignment.noticeSettlementDesc')],
              ].map(([title, desc]) => (
                <div key={title} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: GOLD, flexShrink: 0, marginTop: 7 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: INK }}>{title}</div>
                    <div style={{ fontSize: 12, color: MUTED }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'my' && (
          <>
            <Table
              dataSource={items}
              rowKey="id"
              loading={loading}
              size="small"
              scroll={{ x: 700 }}
              pagination={{
                current: page, total, pageSize: 10,
                onChange: p => fetchItems(p),
                size: 'small', showSizeChanger: false,
              }}
              columns={[
                {
                  title: t('consignment.itemName'), dataIndex: 'item_name', width: 200,
                  render: (v, r) => (
                    <div style={{ cursor: 'pointer' }} onClick={() => setDetail(r)}>
                      <div style={{ fontSize: 12, color: MUTED }}>{r.brand || '—'}</div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{v}</div>
                    </div>
                  ),
                },
                { title: t('consignment.itemNo'), dataIndex: 'item_no', width: 130, render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{v}</span> },
                {
                  title: t('consignment.sellingPrice'), dataIndex: 'price_cny', width: 100,
                  render: v => <span style={{ fontFamily: 'var(--mono)', color: GOLD }}>¥{Number(v).toLocaleString()}</span>,
                },
                {
                  title: t('consignment.status'), dataIndex: 'status', width: 80,
                  render: s => <Tag color={STATUS_MAP[s]?.color}>{t(STATUS_MAP[s]?.labelKey) || s}</Tag>,
                },
                {
                  title: t('consignment.actions'), width: 120,
                  render: (_, r) => (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <a onClick={() => setDetail(r)} style={{ fontSize: 12 }}>{t('common.detail')}</a>
                      {['pending', 'reviewing', 'active'].includes(r.status) && (
                        <a onClick={() => openEdit(r)} style={{ fontSize: 12, color: GOLD }}>{t('consignment.adjustPrice')}</a>
                      )}
                      {['pending', 'reviewing'].includes(r.status) && (
                        <a onClick={() => handleCancel(r.id)} style={{ fontSize: 12, color: '#999' }}>{t('common.cancel')}</a>
                      )}
                    </div>
                  ),
                },
              ]}
            />

            {myOrders.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: INK, marginBottom: 10 }}>{t('consignment.soldOrders')}</div>
                <Table
                  dataSource={myOrders} rowKey="id" size="small" pagination={false}
                  scroll={{ x: 600 }}
                  columns={[
                    { title: t('orders.orderNo'), dataIndex: 'order_no', width: 140, render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{v}</span> },
                    { title: t('consignment.itemName'), dataIndex: 'item_name', width: 160 },
                    { title: t('consignment.dealPrice'), dataIndex: 'price_cny', width: 100, render: v => <span style={{ fontFamily: 'var(--mono)', color: GOLD }}>¥{Number(v).toLocaleString()}</span> },
                    { title: t('consignment.actualReceived'), dataIndex: 'seller_revenue', width: 100, render: v => <span style={{ fontFamily: 'var(--mono)', color: '#2A6645' }}>¥{Number(v).toLocaleString()}</span> },
                    {
                      title: t('consignment.status'), dataIndex: 'status', width: 80,
                      render: s => {
                        const m = { pending_payment: t('orders.statusPending'), paid: t('orders.statusPaid'), shipping: t('orders.statusShipping'), delivered: t('orders.statusDelivered'), completed: t('orders.statusCompleted'), cancelled: t('orders.statusCancelled') };
                        return <Tag>{m[s] || s}</Tag>;
                      },
                    },
                  ]}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Price Modal */}
      <Modal open={!!editItem} onCancel={() => setEditItem(null)} title={t('consignment.adjustPrice')} width={400}
        onOk={handleEditSave} confirmLoading={editSaving} okText={t('common.save')} cancelText={t('common.cancel')}>
        {editItem && (
          <div>
            <div style={{ fontSize: 13, color: MUTED, marginBottom: 12 }}>
              {editItem.item_name}（{editItem.item_no}）
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('consignment.sellingPriceCny')} *</label>
              <input type="number" value={editForm.price_cny}
                onChange={e => setEditForm({ ...editForm, price_cny: e.target.value })}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${BD}`, borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: INK, display: 'block', marginBottom: 4 }}>{t('consignment.minAcceptPrice')}</label>
              <input type="number" value={editForm.min_price_cny}
                onChange={e => setEditForm({ ...editForm, min_price_cny: e.target.value })}
                placeholder={t('consignment.minPricePlaceholder')}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${BD}`, borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ fontSize: 12, color: MUTED, padding: '6px 0', borderTop: `1px solid ${BD}` }}>
              {t('consignment.priceChangeNote')}
            </div>
          </div>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal open={!!detail} onCancel={() => setDetail(null)} footer={null} title={t('consignment.consignmentDetail')} width={520}>
        {detail && (
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label={t('consignment.itemNo')} span={2}><span style={{ fontFamily: 'var(--mono)' }}>{detail.item_no}</span></Descriptions.Item>
            <Descriptions.Item label={t('consignment.itemName')}>{detail.item_name}</Descriptions.Item>
            <Descriptions.Item label={t('consignment.brand')}>{detail.brand || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('consignment.category')}>{detail.category || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('consignment.condition')}>{detail.item_grade || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('consignment.sellingPrice')}>¥{Number(detail.price_cny).toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label={t('consignment.commissionLabel')}>{detail.commission_pct || 8}%</Descriptions.Item>
            <Descriptions.Item label={t('consignment.pricingMode')}>{{ fixed: t('market.fixedPrice'), offer: t('market.negotiable'), auction: t('market.auctionMode') }[detail.pricing_mode] || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('consignment.storageType')}>{detail.storage_type === 'platform' ? t('consignment.platformWarehouse') : t('consignment.selfStorage')}</Descriptions.Item>
            <Descriptions.Item label={t('consignment.status')}><Tag color={STATUS_MAP[detail.status]?.color}>{t(STATUS_MAP[detail.status]?.labelKey)}</Tag></Descriptions.Item>
            {detail.admin_notes && (
              <Descriptions.Item label={t('consignment.reviewNotes')} span={2}><span style={{ color: '#C41D1D' }}>{detail.admin_notes}</span></Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}
